import CreditCardForm from "features/CreditCardForm";
import { FormikProvider, useFormik } from "formik";
import { withStripe, withStripeElements } from "HOCs/withStripe";
import useOpenClose from "hooks/useOpenClose";
import PhoneInput from "library/Atoms/Input/PhoneInput";
import TextInput from "library/Atoms/Input/TextInput";
import Toggler from "library/Atoms/Switch/Toggler";
import Tooltip from "library/Atoms/Tooltip";
import { first, isEmpty } from "lodash";
import BookingFormLayout from "pages/NewOrder/layouts/FormLayout/BookingFormLayout/BookingFormLayout";
import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { retrieveContact, retrieveContactPaymentType } from "store/features/contact.store";
import { DEFAULT_USER_DETAILS, LIBRARY_STORAGE_LIMIT } from "../../../../constants";
import useNewOrderMachine from "../../hooks/useNewOrderMachine";
import NewOrderDetails from "../NewOrderDetails/NewOrderDetails";
import NewOrderSubmitButtons from "../NewOrderSubmitButtons/NewOrderSubmitButtons";
import BookingFormSchema from "./BookingForm.validation";

const BookingForm = ({ onReset, stripeElements, stripe }) => {
  const { t } = useTranslation();

  const contact = useSelector(retrieveContact);
  const { isCheckoutPaymentType } = useSelector(retrieveContactPaymentType);

  const [
    { customer, loading: isSubmitting, checkout, save },
    ,
    { onToggleShouldSaveBooking, onSubmitStep, onReinitializeCheckout },
  ] = useNewOrderMachine();

  const bookingForm = useFormik({
    initialValues: {
      ...(isEmpty(customer) ? DEFAULT_USER_DETAILS : customer),
      card: {
        cards: checkout.cards ?? [],
        defaultCard: first(checkout.cards)?.id ?? null,
        expire_at: checkout.expire_at,
        cardReady: !isCheckoutPaymentType,
      },
    },
    onSubmit: ({ card: { defaultCard }, ...values }) => {
      if (isCheckoutPaymentType) onSubmitStep({ ...values, utils: { elements: stripeElements, stripe, defaultCard } });
      else onSubmitStep(values);
    },
    validationSchema: BookingFormSchema,
    validateOnMount: true,
    enableReinitialize: true,
  });
  const { values, setFieldValue, isValid, submitForm: submitBookingForm } = bookingForm;

  const [isSessionRefreshing, startSessionRefreshing, stopSessionRefreshing] = useOpenClose(false);

  const isStorageLimitExceeded = useMemo(() => {
    return contact.library?.orders >= LIBRARY_STORAGE_LIMIT;
  }, [contact.library?.orders]);

  const onToggleSaveSetting = useCallback(
    (newValue) => onToggleShouldSaveBooking(newValue),
    [onToggleShouldSaveBooking]
  );
  const onToggleCardReady = useCallback((value) => setFieldValue("card.cardReady", value), [setFieldValue]);

  const onSelectSavedCard = useCallback((value) => setFieldValue("card.defaultCard", value), [setFieldValue]);

  const onExpressCheckout = useCallback(async () => {
    await onSubmitStep({ ...values, utils: { elements: stripeElements, stripe, expressCheckout: true } });
  }, [onSubmitStep, values, stripeElements, stripe]);

  const handleReinitializeCheckout = useCallback(async () => {
    startSessionRefreshing();
    await onReinitializeCheckout()
      .catch(() => {})
      .finally(() => stopSessionRefreshing());
  }, [onReinitializeCheckout, startSessionRefreshing, stopSessionRefreshing]);

  return (
    <FormikProvider value={bookingForm}>
      <BookingFormLayout
        settingsTitleSection={t("newOrder.forms.bookingForm.afterSubmitSettingsForm.formTitle")}
        settingsFormSection={
          <Toggler
            id="save"
            name="save"
            label={
              <>
                <span>{t("newOrder.forms.bookingForm.afterSubmitSettingsForm.saveBookingLabel")}</span>
                <Tooltip>{t("newOrder.forms.bookingForm.afterSubmitSettingsForm.saveBookingTooltip")}</Tooltip>
              </>
            }
            value={save}
            onChange={onToggleSaveSetting}
            disabled={isStorageLimitExceeded}
          />
        }
        customerTitleSection={t("newOrder.forms.bookingForm.customerForm.formTitle")}
        customerFormSection={
          <>
            <TextInput
              id="name"
              name="name"
              className="mb-4 pb-2"
              label={t("newOrder.forms.bookingForm.customerForm.fullnameLabel")}
              placeholder={t("newOrder.forms.bookingForm.customerForm.fullnamePlaceholder")}
              onChange={setFieldValue}
              disabled={!!customer.name}
            />

            <TextInput
              id="email"
              name="email"
              className="mb-4 pb-2"
              label={t("common.forms.emailLabel")}
              placeholder={t("common.forms.emailPlaceholder")}
              onChange={setFieldValue}
              disabled={!!customer.email}
            />

            <PhoneInput
              id="phone"
              name="phone"
              className="mb-4 pb-2"
              label={t("common.forms.phoneLabel")}
              placeholder={t("common.forms.phonePlaceholder")}
              onChange={setFieldValue}
              disabled={!!customer.phone}
            />
          </>
        }
        cardTitleSection={t("newOrder.forms.bookingForm.paymentForm.formTitle")}
        cardFormSection={
          <CreditCardForm
            name="card"
            onComplete={onToggleCardReady}
            onSelect={onSelectSavedCard}
            onConfirm={onExpressCheckout}
            onRefreshSession={handleReinitializeCheckout}
            error={checkout.error}
            isRefreshing={isSessionRefreshing}
          />
        }
        orderDetailsSection={<NewOrderDetails />}
        controlsSection={
          <NewOrderSubmitButtons
            onSubmit={submitBookingForm}
            onReset={onReset}
            resetBtnOptions={{ disabled: checkout.error }}
            submitBtnOptions={{ disabled: !isValid, loading: isSubmitting }}
          />
        }
        shouldShowCardSections={isCheckoutPaymentType}
      />
    </FormikProvider>
  );
};

BookingForm.defaultProps = {
  stripeElements: null,
  stripe: null,
};

BookingForm.propTypes = {
  onReset: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  stripeElements: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  stripe: PropTypes.any,
};

export default withStripe(withStripeElements(BookingForm));
